// FormResponsesModal — opened from the Forms-page "Responses" button. Lists the
// reusable response sets (GET /form-choices/all) in a searchable/sortable
// DataTable with a "+ Add response set" action opening ResponseSetModal. The
// list returns every set (no server paging), so search/sort/pagination are all
// client-side.
//
// This owns the three set actions — Edit, Duplicate, Delete — and both entry
// points reach them: the selection bar over the table, and each row's kebab.
// Edit and Duplicate take exactly one set, so the bar HIDES them (rather than
// disabling) while 2+ rows are ticked, the same rule FormTemplatesPage's bar
// uses. Delete takes any number: POST /form-choices/bulk-delete is one call
// whether it carries one id or ten, so the kebab's delete is just the bar's with
// a one-element array.
//
// Duplicate has no endpoint of its own — it's a create carrying the source's own
// choices under a "(copy)" title. That suffix is a LITERAL, not an i18n key: it
// is written into the record and persisted, so it must not vary with whichever
// locale the person clicking happened to be using.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Button,
  DataTable,
  Icon,
  IconButton,
  Input,
  ConfirmModal,
  Modal,
  TableFooter,
} from '@/components/common';
import {
  useBulkDeleteFormChoicesMutation,
  useCreateFormChoiceMutation,
  useGetFormChoicesQuery,
  type ResponseSet,
} from '@/api/endpoints/formChoicesApi';
import { useResponseSetColumns } from './responseSetColumns';
import ResponseSetModal from './ResponseSetModal';

interface FormResponsesModalProps {
  open: boolean;
  onClose: () => void;
}

type SortType = 'asc' | 'desc';

const FormResponsesModal = ({ open, onClose }: FormResponsesModalProps) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: string; type: SortType }>({
    key: 'updated',
    type: 'desc',
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editorOpen, setEditorOpen] = useState(false);
  // The set the editor is open for — null means "create".
  const [editing, setEditing] = useState<ResponseSet | null>(null);
  // The ids a confirmed delete would remove; null while no confirm is open.
  const [deleting, setDeleting] = useState<string[] | null>(null);

  const [createFormChoice] = useCreateFormChoiceMutation();
  const [bulkDelete, { isLoading: deletingBusy }] = useBulkDeleteFormChoicesMutation();

  const {
    data: sets = [],
    isLoading,
    isError,
    isFetching,
  } = useGetFormChoicesQuery(undefined, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  });

  // Reset to a clean state each time the modal (re)opens.
  useEffect(() => {
    if (open) {
      setPage(1);
      setSearch('');
      setSelectedIds(new Set());
      setEditorOpen(false);
      setEditing(null);
      setDeleting(null);
    }
  }, [open]);

  const openEditor = (set: ResponseSet | null) => {
    setEditing(set);
    setEditorOpen(true);
  };

  // No duplicate endpoint: a copy is a create carrying the source's choices. The
  // "(copy)" suffix is a stored record value, so it stays literal — see header.
  const duplicate = async (set: ResponseSet) => {
    try {
      await createFormChoice({ title: `${set.name} (copy)`, choices: set.choices }).unwrap();
      toast.success(t('forms_responses_created'));
    } catch {
      toast.error(t('error_generic'));
    }
  };

  // One request either way, so there is no partial outcome to reconcile: on
  // failure the selection is left intact and a retry needs no re-ticking.
  const runDelete = async () => {
    if (!deleting) return;
    try {
      await bulkDelete(deleting).unwrap();
      toast.success(t('forms_responses_deleted', { count: deleting.length }));
      setSelectedIds(new Set());
      setDeleting(null);
    } catch {
      toast.error(t('error_generic'));
      setDeleting(null);
    }
  };

  const columns = useResponseSetColumns({
    onEdit: openEditor,
    onDuplicate: duplicate,
    onDelete: (row) => setDeleting([row.id]),
  });

  // Client-side search (on set name) → sort → paginate.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q ? sets.filter((s) => s.name.toLowerCase().includes(q)) : sets;
    const dir = sort.type === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sort.key === 'name') return a.name.localeCompare(b.name) * dir;
      if (sort.key === 'options') return (a.choices.length - b.choices.length) * dir;
      // updated
      return ((Date.parse(a.updatedAt ?? '') || 0) - (Date.parse(b.updatedAt ?? '') || 0)) * dir;
    });
  }, [sets, search, sort]);

  const total = filtered.length;
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const pageRows = filtered.slice((page - 1) * limit, page * limit);

  // A new search / sort shrinks or reorders the set — snap back to page 1.
  useEffect(() => setPage(1), [search, sort]);

  // A delete can empty the page we're standing on — fall back to the last one
  // that still has rows rather than showing an empty table with a live pager.
  useEffect(() => {
    const last = Math.max(1, Math.ceil(total / limit));
    if (page > last) setPage(last);
  }, [total, limit, page]);

  // Edit and Duplicate need the row itself, not just its id.
  const onlySelected =
    selectedIds.size === 1 ? (sets.find((s) => selectedIds.has(s.id)) ?? null) : null;

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const pageIds = pageRows.map((r) => r.id);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const someSelected = pageIds.some((id) => selectedIds.has(id));
  const toggleSelectAll = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });

  // Header click / column header — same key toggles direction, new key → desc.
  const handleSort = (key: string) =>
    setSort((prev) =>
      prev.key === key
        ? { key, type: prev.type === 'asc' ? 'desc' : 'asc' }
        : { key, type: 'desc' },
    );

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        className='form-responses'
        icon='list-checks'
        title={t('forms_responses_title')}
        subtitle={t('forms_responses_subtitle')}
        size='lg'
      >
        <div className='form-responses__toolbar'>
          <div className='form-responses__search'>
            <Input
              size='sm'
              iconStart='search'
              placeholder={t('forms_responses_search_ph')}
              aria-label={t('forms_responses_search_ph')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <IconButton
                className='form-responses__search-clear'
                icon='x'
                variant='ghost'
                size='sm'
                aria-label={t('forms_search_clear')}
                onClick={() => setSearch('')}
              />
            )}
          </div>
          <div className='form-responses__actions'>
            <span className='form-responses__count'>
              {t('forms_responses_count', { count: sets.length })}
            </span>
            <Button iconStart='plus' onClick={() => openEditor(null)}>
              {t('forms_responses_add')}
            </Button>
          </div>
        </div>

        {selectedIds.size > 0 && (
          <div className='form-responses__selection-bar'>
            <div className='form-responses__selection-lead'>
              <span className='form-responses__selected'>
                {t('forms_selected_count', { count: selectedIds.size })}
              </span>
              <span className='form-responses__selection-divider' />
              {/* Edit and Duplicate target one set — hidden while 2+ are picked. */}
              {onlySelected && (
                <>
                  <Button
                    variant='ghost'
                    iconStart='pencil'
                    onClick={() => openEditor(onlySelected)}
                  >
                    {t('forms_action_edit')}
                  </Button>
                  <Button variant='ghost' iconStart='copy' onClick={() => duplicate(onlySelected)}>
                    {t('forms_action_duplicate')}
                  </Button>
                </>
              )}
              <Button
                variant='ghost'
                className='form-responses__selection-delete'
                iconStart='trash-2'
                onClick={() => setDeleting([...selectedIds])}
              >
                {t('forms_action_delete')}
              </Button>
            </div>
            <Button variant='ghost' onClick={() => setSelectedIds(new Set())}>
              {t('button_clear')}
            </Button>
          </div>
        )}

        <DataTable
          columns={columns}
          rows={pageRows}
          rowKey={(r) => r.id}
          selectable
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          allSelected={allSelected}
          someSelected={someSelected}
          onToggleSelectAll={toggleSelectAll}
          selectAllLabel={t('forms_select_all')}
          sort={sort}
          onSort={handleSort}
          loading={isLoading || isFetching}
          error={isError}
          loadingText={t('text_loading')}
          errorText={t('error_generic')}
          minWidth={840}
          empty={
            <>
              <Icon name='list-checks' size={26} />
              {t('forms_responses_empty')}
            </>
          }
        />

        <TableFooter
          page={page}
          limit={limit}
          total={total}
          limits={[10, 20]}
          onPage={setPage}
          onLimit={(n) => {
            setLimit(n);
            setPage(1);
          }}
          perPageLabel={t('forms_items_per_page')}
          rangeText={t('forms_responses_range', { from, to, total })}
        />
      </Modal>

      <ResponseSetModal open={editorOpen} onClose={() => setEditorOpen(false)} set={editing} />

      {deleting && (
        <ConfirmModal
          open
          onClose={() => setDeleting(null)}
          onConfirm={runDelete}
          tone='danger'
          icon='trash-2'
          title={t('forms_responses_delete_title', { count: deleting.length })}
          description={t('forms_responses_delete_body', { count: deleting.length })}
          confirmLabel={t('forms_action_delete')}
          confirmVariant='danger'
          loading={deletingBusy}
        />
      )}
    </>
  );
};

export default FormResponsesModal;
